/**
 * ResultsDisplay Component
 *
 * Purpose: Shows FSS certification requirements and compliance information
 * User Flow: This is Step 3 - User sees their personalised results
 * Data Source: calculation engine with all data sources
 */

import React, { useState } from "react";
import { useCalculatorContext } from "../contexts/CalculatorContext";
import {
  generateShareableUrl,
  generateMailtoLink,
} from "../utils/urlGenerator";

const ResultsDisplay = () => {
  const { state, actions } = useCalculatorContext();
  const [expandedSection, setExpandedSection] = useState(null);
  const results = state.calculationResults;

  if (!results) {
    return (
      <div className="fss-results-display">
        <div className="fss-no-results">
          <p>
            No calculation results available. Please complete the previous
            steps.
          </p>
          <button onClick={actions.reset} className="fss-btn fss-btn-primary">
            Start Over
          </button>
        </div>
      </div>
    );
  }

  const { compliance, training, stateSpecific, ongoing, warnings, links } =
    results;

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Handle share
  const handleShare = () => {
    const shareUrl = generateShareableUrl(
      state.selectedState,
      state.selectedBusinessType
    );
    navigator.clipboard.writeText(shareUrl);
    alert("Link copied to clipboard!");
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fss-results-display">
      {/* Results header */}
      <div className="fss-results-header">
        <h1>Your Food Safety Supervisor Requirements</h1>
        <p className="fss-results-subtitle">
          Personalised requirements for {compliance.stateName} -{" "}
          {compliance.businessType}
        </p>
      </div>

      {/* Action buttons */}
      <div className="fss-results-actions">
        <button
          onClick={handlePrint}
          className="fss-btn fss-btn-secondary"
          aria-label="Print results"
        >
          🖨️ Print
        </button>
        <button
          onClick={handleShare}
          className="fss-btn fss-btn-secondary"
          aria-label="Share results"
        >
          🔗 Share
        </button>
        <a
          href={generateMailtoLink(results)}
          className="fss-btn fss-btn-secondary"
          aria-label="Email results"
        >
          📧 Email
        </a>
        <button
          onClick={actions.reset}
          className="fss-btn fss-btn-outline"
          aria-label="Start over"
        >
          ↻ Start Over
        </button>
      </div>

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <div className="fss-warnings-section">
          {warnings.map((warning, index) => (
            <div key={index} className={`fss-alert fss-alert-${warning.type}`}>
              {warning.title && <strong>{warning.title}: </strong>}
              {warning.message}
            </div>
          ))}
        </div>
      )}

      {/* Compliance Summary */}
      <div className="fss-results-wrapper">
        <section className="fss-results-section fss-section-summary">
          <h2>📋 Compliance Summary</h2>
          <div className="fss-summary-grid">
            <div className="fss-summary-item">
              <span className="fss-summary-label">FSS Required:</span>
              <span
                className={`fss-summary-value ${compliance.fssRequired ? "fss-required" : "fss-optional"}`}
              >
                {compliance.fssRequired ? "✓ Yes" : "⚠️ Recommended"}
              </span>
            </div>
            <div className="fss-summary-item">
              <span className="fss-summary-label">State:</span>
              <span className="fss-summary-value">{compliance.stateName}</span>
            </div>
            <div className="fss-summary-item">
              <span className="fss-summary-label">Business Type:</span>
              <span className="fss-summary-value">
                {compliance.businessType}
              </span>
            </div>
            <div className="fss-summary-item">
              <span className="fss-summary-label">Category:</span>
              <span className="fss-summary-value">{compliance.category}</span>
            </div>
            {compliance.highRisk && (
              <div className="fss-summary-item fss-highlight">
                <span className="fss-summary-label">Risk Level:</span>
                <span className="fss-summary-value">High-Risk Business</span>
              </div>
            )}
          </div>
          {compliance.fssRequiredNote && (
            <div className="fss-summary-note">
              <em>{compliance.fssRequiredNote}</em>
            </div>
          )}
        </section>
      </div>

      {/* Training Requirements */}
      <div className="fss-results-wrapper">
      <section className="fss-results-section fss-section-training">
        <h2>📚 Required Training</h2>

        <div className="fss-training-overview">
          <div className="fss-training-stat">
            <strong>Skill Set:</strong> {training.skillSetCode}
          </div>
          <div className="fss-training-stat">
            <strong>Total Duration:</strong> {training.totalDuration} hours
          </div>
          <div className="fss-training-stat">
            <strong>Assessment:</strong> {training.assessmentType}
          </div>
        </div>

        <div className="fss-units-list">
          <h3>Required Units:</h3>
          {training.units.map((unit) => (
            <div key={unit.code} className="fss-unit-item">
              <div className="fss-unit-code">{unit.code}</div>
              <div className="fss-unit-details">
                <div className="fss-unit-name">{unit.name}</div>
                <div className="fss-unit-meta">
                  <span>{unit.industrySector}</span> • <span>{unit.level}</span>{" "}
                  • <span>{unit.durationHours} hours</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {training.notes && training.notes.length > 0 && (
          <div className="fss-training-notes">
            <h4>Important Notes:</h4>
            {training.notes.map((note, idx) => (
              <div key={idx} className="fss-note-item">
                ℹ️ {note}
              </div>
            ))}
          </div>
        )}
      </section>
      </div>

      {/* State Requirements */}
      <div className="fss-results-wrapper">
      <section className="fss-results-section fss-section-state">
        <button
          className="fss-section-header fss-collapsible"
          onClick={() => toggleSection("state")}
          aria-expanded={expandedSection === "state"}
        >
          <h2>🏛️ State-Specific Requirements</h2>
          <span className="fss-expand-icon">
            {expandedSection === "state" ? "−" : "+"}
          </span>
        </button>

        <div
          className={`fss-section-content ${expandedSection === "state" ? "fss-expanded" : ""}`}
        >
          <div className="fss-requirements-grid">
            <div className="fss-requirement-item">
              <strong>Certificate Validity:</strong>{" "}
              {stateSpecific.validityYears} years
            </div>
            <div className="fss-requirement-item">
              <strong>Appointment Deadline:</strong> Within{" "}
              {stateSpecific.appointmentDeadlineDays} days
            </div>
            <div className="fss-requirement-item">
              <strong>Display Required:</strong>{" "}
              {stateSpecific.displayRequired
                ? `Yes - ${stateSpecific.displayDetails}`
                : "No"}
            </div>
            <div className="fss-requirement-item">
              <strong>Council Notification:</strong>{" "}
              {stateSpecific.councilNotification
                ? `Yes - ${stateSpecific.councilNotificationDetails}`
                : "No"}
            </div>
            <div className="fss-requirement-item">
              <strong>Standard 3.2.2A Deadline:</strong>{" "}
              {stateSpecific.standardDeadline}
              {stateSpecific.standardDeadlineExtended && (
                <span className="fss-badge">Extended</span>
              )}
            </div>
            <div className="fss-requirement-item">
              <strong>Audit Requirements:</strong>{" "}
              {stateSpecific.auditRequirements}
            </div>
          </div>

          {stateSpecific.focusAreas && stateSpecific.focusAreas.length > 0 && (
            <div className="fss-focus-areas">
              <h4>Key Focus Areas:</h4>
              <ul>
                {stateSpecific.focusAreas.map((area, idx) => (
                  <li key={idx}>{area}</li>
                ))}
              </ul>
            </div>
          )}

          {stateSpecific.specialRequirements &&
            stateSpecific.specialRequirements.length > 0 && (
              <div className="fss-special-requirements">
                <h4>Special Requirements:</h4>
                <ul>
                  {stateSpecific.specialRequirements.map((req, idx) => (
                    <li key={idx}>{req}</li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      </section>
      </div>

      {/* Ongoing Compliance */}
      <div className="fss-results-wrapper">
      <section className="fss-results-section fss-section-ongoing">
        <button
          className="fss-section-header fss-collapsible"
          onClick={() => toggleSection("ongoing")}
          aria-expanded={expandedSection === "ongoing"}
        >
          <h2>🔄 Ongoing Compliance & Recertification</h2>
          <span className="fss-expand-icon">
            {expandedSection === "ongoing" ? "−" : "+"}
          </span>
        </button>

        <div
          className={`fss-section-content ${expandedSection === "ongoing" ? "fss-expanded" : ""}`}
        >
          <div className="fss-ongoing-grid">
            <div className="fss-ongoing-item">
              <strong>Recertification Required:</strong> Every{" "}
              {ongoing.recertificationPeriod} years
            </div>
            <div className="fss-ongoing-item">
              <strong>Method:</strong> {ongoing.method}
            </div>
            <div className="fss-ongoing-item">
              <strong>Grace Period:</strong> {ongoing.gracePeriodDays} days
            </div>
            <div className="fss-ongoing-item">
              <strong>Duration:</strong> {ongoing.durationHours} hours
            </div>
            <div className="fss-ongoing-item">
              <strong>Estimated Cost:</strong> {ongoing.costRange}
            </div>
            <div className="fss-ongoing-item">
              <strong>Full Course Alternative:</strong>{" "}
              {ongoing.fullCourseAlternative ? "Yes" : "No"}
            </div>
          </div>
        </div>
      </section>
      </div>

      {/* Governing Body & Penalties */}
      <div className="fss-results-wrapper">
      <section className="fss-results-section fss-section-authority">
        <button
          className="fss-section-header fss-collapsible"
          onClick={() => toggleSection("authority")}
          aria-expanded={expandedSection === "authority"}
        >
          <h2>⚖️ Governing Authority & Penalties</h2>
          <span className="fss-expand-icon">
            {expandedSection === "authority" ? "−" : "+"}
          </span>
        </button>

        <div
          className={`fss-section-content ${expandedSection === "authority" ? "fss-expanded" : ""}`}
        >
          <div className="fss-authority-info">
            <div className="fss-authority-item">
              <strong>Governing Body:</strong>
              <a
                href={stateSpecific.governingBody.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                {stateSpecific.governingBody.name} ↗
              </a>
            </div>
            <div className="fss-authority-item fss-penalty">
              <strong>Penalties (Individual):</strong>{" "}
              {stateSpecific.penalties.individual}
            </div>
            <div className="fss-authority-item fss-penalty">
              <strong>Penalties (Company):</strong>{" "}
              {stateSpecific.penalties.company}
            </div>
          </div>
        </div>
      </section>
      </div>

      {/* Call to Action */}
      <div className="fss-results-wrapper">
      <section className="fss-results-section fss-section-cta">
        <h2>🎓 Ready to Get Certified?</h2>
        <p>Start your Food Safety Supervisor training with CFT today.</p>

        <div className="fss-cta-buttons">
          <a
            href={links.cftCourse}
            className="fss-btn fss-btn-primary fss-btn-large"
            target="_blank"
            rel="noopener noreferrer"
          >
            View CFT Training Course →
          </a>
        </div>
        <div className="fss-more-info-buttons">
          <a
            href={links.stateAuthority}
            className="fss-btn fss-btn-secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            {stateSpecific.governingBody.name} Website ↗
          </a>
          <a
            href={links.standard322a}
            className="fss-btn fss-btn-secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Standard 3.2.2A Info ↗
          </a>
        </div>

        <div className="fss-help-cta">
          <p>
            <strong>Need help or have questions?</strong>
            <br />
            Contact CFT for personalised guidance on your FSS requirements.
          </p>
          <a href="/contact" className="fss-btn fss-btn-outline">
            Contact CFT
          </a>
        </div>
      </section>
      </div>

      {/* Footer note */}
      <div className="fss-results-footer">
        <p className="fss-disclaimer">
          <strong>Important:</strong> This calculator provides general guidance
          based on current regulations. Requirements may change. Please verify
          with your state's food authority and check the official legislation
          links provided above.
        </p>
        <p className="fss-last-updated">
          Information current as of October 2025
        </p>
      </div>
    </div>
  );
};

export default ResultsDisplay;
